import { useState } from "react";
import logo from "../assets/logo.png";
import { ArrowLeft, Eye, EyeOff, Mail, Lock, UserPlus, LogIn } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { ImageWithFallback } from "./figma/ImageWithFallback";
import { BASE_URL, postWithoutTokenAPI } from "../util/Api";
import "../styles/EmailLoginPage.css";
import toast, { Toaster } from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { setUserId } from "../redux/userSlice";

export function EmailLoginPage() {
  const [activeTab, setActiveTab] = useState("signin"); // 'signin' | 'signup'
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // ── Sign In State ──────────────────────────────────────────────────
  const [signInData, setSignInData] = useState({ email: "", password: "" });
  const [signInErrors, setSignInErrors] = useState({});
  const [signInLoading, setSignInLoading] = useState(false);
  const [showSignInPassword, setShowSignInPassword] = useState(false);

  // ── Sign Up State ──────────────────────────────────────────────────
  const [signUpData, setSignUpData] = useState({ email: "", password: "", confirmPassword: "" });
  const [signUpErrors, setSignUpErrors] = useState({});
  const [signUpLoading, setSignUpLoading] = useState(false);
  const [showSignUpPassword, setShowSignUpPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // ── Helpers ────────────────────────────────────────────────────────
  const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  // ── Sign In Handlers ───────────────────────────────────────────────
  const handleSignInChange = (e) => {
    setSignInData({ ...signInData, [e.target.name]: e.target.value });
    if (signInErrors[e.target.name]) {
      setSignInErrors({ ...signInErrors, [e.target.name]: "" });
    }
  };

  const validateSignIn = () => {
    const errs = {};
    if (!signInData.email) errs.email = "Email is required";
    else if (!isValidEmail(signInData.email)) errs.email = "Enter a valid email address";
    if (!signInData.password) errs.password = "Password is required";
    return errs;
  };

  const handleSignIn = async (e) => {
    e.preventDefault();
    const errs = validateSignIn();
    if (Object.keys(errs).length) { setSignInErrors(errs); return; }

    setSignInLoading(true);
    try {
      const response = await postWithoutTokenAPI(
        BASE_URL + "/auth/email/login",
        { email: signInData.email.trim().toLowerCase(), password: signInData.password }
      );

      if (response?.statusCode === 200 && response?.data) {
        const userData = response.data;
        dispatch(setUserId(userData));
        localStorage.setItem("UserId", JSON.stringify({
          id: userData,
          timestamp: new Date().toISOString(),
        }));
        toast.success("Login successful! Welcome back 🎉");
        if (!userData?.profile_completed) {
          navigate("/register", { state: { verifiedUser: userData.accessToken } });
        } else {
          navigate("/");
        }
      } else {
        const msg = response?.message || "Invalid email or password";
        toast.error(msg);
        setSignInErrors({ general: msg });
      }
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSignInLoading(false);
    }
  };

  // ── Sign Up Handlers ───────────────────────────────────────────────
  const handleSignUpChange = (e) => {
    setSignUpData({ ...signUpData, [e.target.name]: e.target.value });
    if (signUpErrors[e.target.name]) {
      setSignUpErrors({ ...signUpErrors, [e.target.name]: "" });
    }
  };

  const validateSignUp = () => {
    const errs = {};
    if (!signUpData.email) errs.email = "Email is required";
    else if (!isValidEmail(signUpData.email)) errs.email = "Enter a valid email address";
    if (!signUpData.password) errs.password = "Password is required";
    else if (signUpData.password.length < 8) errs.password = "Password must be at least 8 characters";
    if (!signUpData.confirmPassword) errs.confirmPassword = "Please confirm your password";
    else if (signUpData.password !== signUpData.confirmPassword) errs.confirmPassword = "Passwords do not match";
    return errs;
  };

  const handleSignUp = async (e) => {
    e.preventDefault();
    const errs = validateSignUp();
    if (Object.keys(errs).length) { setSignUpErrors(errs); return; }

    setSignUpLoading(true);
    try {
      const response = await postWithoutTokenAPI(
        BASE_URL + "/auth/email/register",
        { email: signUpData.email.trim().toLowerCase(), password: signUpData.password }
      );

      if (response?.statusCode === 201 && response?.data) {
        const userData = response.data;
        dispatch(setUserId(userData));
        localStorage.setItem("UserId", JSON.stringify({
          id: userData,
          timestamp: new Date().toISOString(),
        }));
        toast.success("Account created successfully! 🎊");
        navigate("/register", { state: { verifiedUser: userData.accessToken } });
      } else {
        const msg = response?.message || "Registration failed. Please try again.";
        toast.error(msg);
        setSignUpErrors({ general: msg });
      }
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSignUpLoading(false);
    }
  };

  return (
    <>
      <Toaster position="top-right" />

      {/* ── Top Nav Bar ──────────────────────────────────────────────── */}
      <div className="email-login-nav">
        <button onClick={() => navigate(-1)} className="email-login-back-btn">
          <ArrowLeft size={18} />
          <span>Back</span>
        </button>
      </div>

      {/* ── Main Wrapper ─────────────────────────────────────────────── */}
      <div className="email-login-wrapper">
        {/* Left Image Panel */}
        <div className="email-login-left">
          <ImageWithFallback
            src="/Images/authentic-indian-bride-groom.jpg"
            alt="Couple"
            className="email-login-image"
          />
          <div className="email-login-left-overlay">
            <div className="email-login-left-content">
              <div className="email-login-left-badge">✨ Email Login</div>
              <h2>Your Perfect Match<br />Awaits You</h2>
              <p>Join thousands of happy couples who found their life partner on Aishwarya matrimony.</p>
            </div>
          </div>
        </div>

        {/* Right Form Panel */}
        <div className="email-login-right">
          <Card className="email-login-card">
            <CardHeader className="email-login-card-header">
              <div className="email-login-logo-wrap">
                <img src={logo} alt="Aishwarya matrimony" className="email-login-logo" />
              </div>
              <CardTitle className="email-login-title">
                {activeTab === "signin" ? "Welcome Back" : "Create Account"}
              </CardTitle>
              <p className="email-login-subtitle">
                {activeTab === "signin"
                  ? "Sign in with your email and password"
                  : "Register with your email to get started"}
              </p>
            </CardHeader>

            <CardContent className="email-login-card-content">
              {/* ── Tab Switch ─────────────────────────────────────── */}
              <div className="email-login-tabs">
                <button
                  className={`email-tab-btn ${activeTab === "signin" ? "active" : ""}`}
                  onClick={() => { setActiveTab("signin"); setSignInErrors({}); }}
                >
                  <LogIn size={15} />
                  Sign In
                </button>
                <button
                  className={`email-tab-btn ${activeTab === "signup" ? "active" : ""}`}
                  onClick={() => { setActiveTab("signup"); setSignUpErrors({}); }}
                >
                  <UserPlus size={15} />
                  Sign Up
                </button>
              </div>

              {/* ── Sign In Form ────────────────────────────────────── */}
              {activeTab === "signin" && (
                <form onSubmit={handleSignIn} className="email-login-form" noValidate>
                  {signInErrors.general && (
                    <div className="email-login-error-banner">{signInErrors.general}</div>
                  )}

                  <div className="email-field-group">
                    <Label htmlFor="signin-email" className="email-field-label">
                      <Mail size={14} /> Email Address
                    </Label>
                    <Input
                      id="signin-email"
                      name="email"
                      type="email"
                      placeholder="Enter your email address"
                      value={signInData.email}
                      onChange={handleSignInChange}
                      className={`email-login-input ${signInErrors.email ? "input-error" : ""}`}
                      autoComplete="email"
                    />
                    {signInErrors.email && <span className="email-field-error">{signInErrors.email}</span>}
                  </div>

                  <div className="email-field-group">
                    <Label htmlFor="signin-password" className="email-field-label">
                      <Lock size={14} /> Password
                    </Label>
                    <div className="email-password-wrap">
                      <Input
                        id="signin-password"
                        name="password"
                        type={showSignInPassword ? "text" : "password"}
                        placeholder="Enter your password"
                        value={signInData.password}
                        onChange={handleSignInChange}
                        className={`email-login-input ${signInErrors.password ? "input-error" : ""}`}
                        autoComplete="current-password"
                      />
                      <button
                        type="button"
                        className="email-eye-btn"
                        onClick={() => setShowSignInPassword(!showSignInPassword)}
                        tabIndex={-1}
                      >
                        {showSignInPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                    {signInErrors.password && <span className="email-field-error">{signInErrors.password}</span>}
                  </div>

                  <Button
                    type="submit"
                    disabled={signInLoading}
                    className="email-login-submit-btn"
                  >
                    {signInLoading ? (
                      <span className="email-btn-loader">
                        <span className="email-spinner" /> Signing in...
                      </span>
                    ) : (
                      <>
                        <LogIn size={16} />
                        Sign In
                      </>
                    )}
                  </Button>

                  <p className="email-switch-hint">
                    Don't have an account?{" "}
                    <button type="button" className="email-switch-link" onClick={() => setActiveTab("signup")}>
                      Create one here
                    </button>
                  </p>
                </form>
              )}

              {/* ── Sign Up Form ────────────────────────────────────── */}
              {activeTab === "signup" && (
                <form onSubmit={handleSignUp} className="email-login-form" noValidate>
                  {signUpErrors.general && (
                    <div className="email-login-error-banner">{signUpErrors.general}</div>
                  )}

                  <div className="email-field-group">
                    <Label htmlFor="signup-email" className="email-field-label">
                      <Mail size={14} /> Email Address
                    </Label>
                    <Input
                      id="signup-email"
                      name="email"
                      type="email"
                      placeholder="Enter your email address"
                      value={signUpData.email}
                      onChange={handleSignUpChange}
                      className={`email-login-input ${signUpErrors.email ? "input-error" : ""}`}
                      autoComplete="email"
                    />
                    {signUpErrors.email && <span className="email-field-error">{signUpErrors.email}</span>}
                  </div>

                  <div className="email-field-group">
                    <Label htmlFor="signup-password" className="email-field-label">
                      <Lock size={14} /> Password
                    </Label>
                    <div className="email-password-wrap">
                      <Input
                        id="signup-password"
                        name="password"
                        type={showSignUpPassword ? "text" : "password"}
                        placeholder="Create a strong password (min. 8 chars)"
                        value={signUpData.password}
                        onChange={handleSignUpChange}
                        className={`email-login-input ${signUpErrors.password ? "input-error" : ""}`}
                        autoComplete="new-password"
                      />
                      <button
                        type="button"
                        className="email-eye-btn"
                        onClick={() => setShowSignUpPassword(!showSignUpPassword)}
                        tabIndex={-1}
                      >
                        {showSignUpPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                    {signUpErrors.password && <span className="email-field-error">{signUpErrors.password}</span>}
                  </div>

                  <div className="email-field-group">
                    <Label htmlFor="confirm-password" className="email-field-label">
                      <Lock size={14} /> Confirm Password
                    </Label>
                    <div className="email-password-wrap">
                      <Input
                        id="confirm-password"
                        name="confirmPassword"
                        type={showConfirmPassword ? "text" : "password"}
                        placeholder="Re-enter your password"
                        value={signUpData.confirmPassword}
                        onChange={handleSignUpChange}
                        className={`email-login-input ${signUpErrors.confirmPassword ? "input-error" : ""}`}
                        autoComplete="new-password"
                      />
                      <button
                        type="button"
                        className="email-eye-btn"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        tabIndex={-1}
                      >
                        {showConfirmPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                    {signUpErrors.confirmPassword && <span className="email-field-error">{signUpErrors.confirmPassword}</span>}
                  </div>

                  {/* Password strength bar */}
                  {signUpData.password && (
                    <div className="email-password-strength">
                      <div className="email-strength-bars">
                        {[1, 2, 3, 4].map((level) => (
                          <div
                            key={level}
                            className={`email-strength-bar ${getStrengthLevel(signUpData.password) >= level
                              ? `strength-${getStrengthLevel(signUpData.password)}`
                              : ""}`}
                          />
                        ))}
                      </div>
                      <span className="email-strength-label">
                        {["", "Weak", "Fair", "Good", "Strong"][getStrengthLevel(signUpData.password)]}
                      </span>
                    </div>
                  )}

                  <Button
                    type="submit"
                    disabled={signUpLoading}
                    className="email-login-submit-btn"
                  >
                    {signUpLoading ? (
                      <span className="email-btn-loader">
                        <span className="email-spinner" /> Creating account...
                      </span>
                    ) : (
                      <>
                        <UserPlus size={16} />
                        Create Account
                      </>
                    )}
                  </Button>

                  <p className="email-switch-hint">
                    Already have an account?{" "}
                    <button type="button" className="email-switch-link" onClick={() => setActiveTab("signin")}>
                      Sign in here
                    </button>
                  </p>
                </form>
              )}


            </CardContent>
          </Card>
        </div>
      </div>
    </>
  );
}

// ── Password Strength Calculator ─────────────────────────────────────────────
function getStrengthLevel(password) {
  if (!password) return 0;
  let score = 0;
  if (password.length >= 8) score++;
  if (/[A-Z]/.test(password)) score++;
  if (/[0-9]/.test(password)) score++;
  if (/[^A-Za-z0-9]/.test(password)) score++;
  return score;
}
